import { useEffect, useMemo, useState } from "react";
import { api, inr } from "../lib/api";
import { PageHead } from "../components/ui-bits";
import { downloadBlob } from "../lib/services";
import { BankImport } from "./BankImport";
import { toast } from "sonner";
import {
  SquaresFour, AddressBook, Package, Receipt, ShoppingCart, Bank, Wallet, ChartBar, Trash, Plus, FilePdf, BookOpenText, Printer, FileCode, EnvelopeSimple
} from "@phosphor-icons/react";

const GST_RATES = [0, 5, 12, 18, 28];
const EXP_HEADS = ["Rent", "Salary & Wages", "Electricity", "Travel", "Office Expenses", "Professional Fees", "Bank Charges", "Other"];
const today = () => new Date().toISOString().slice(0, 10);
const newLine = () => ({ description: "", hsn: "", qty: 1, rate: 0, gst_rate: 18 });
const inv = { date: "", party: "", gstin: "", invoice_no: "", due_date: "", interstate: false, lines: [newLine()] };

const REGISTERS = {
  parties: {
    title: "Party Master", hint: "Customers & suppliers with GSTIN",
    init: { name: "", party_type: "Customer", gstin: "", state: "", phone: "", email: "" },
    fields: [["name", "Party name"], ["party_type", "Type", ["Customer", "Supplier"]], ["gstin", "GSTIN"], ["state", "State"], ["phone", "Phone"], ["email", "Email (for invoices)", "email"]],
    cols: [["name", "Name"], ["party_type", "Type"], ["gstin", "GSTIN"], ["state", "State"], ["phone", "Phone"], ["email", "Email"]],
  },
  items: {
    title: "Item / Service Master", hint: "Goods & services with HSN/SAC and GST rate",
    init: { name: "", hsn: "", unit: "Nos", rate: 0, gst_rate: 18, opening_stock: 0 },
    fields: [["name", "Item name"], ["hsn", "HSN / SAC"], ["unit", "Unit", ["Nos", "Kg", "Ltr", "Mtr", "Box", "Hrs"]], ["rate", "Rate", "number"], ["gst_rate", "GST %", GST_RATES], ["opening_stock", "Opening stock", "number"]],
    cols: [["name", "Item"], ["hsn", "HSN/SAC"], ["unit", "Unit"], ["rate", "Rate", "inr"], ["gst_rate", "GST %"], ["opening_stock", "Opening"]],
  },
  sales: {
    title: "Sales Invoices (Billing)", hint: "Tax invoices — CGST/SGST or IGST computed automatically",
    init: inv,
    fields: [["date", "Date", "date"], ["party", "Customer", "party:Customer"], ["gstin", "Customer GSTIN (blank = B2C)"], ["invoice_no", "Invoice no. (auto)"], ["due_date", "Due date (default +30d)", "date"], ["interstate", "Inter-state (IGST)", "check"]],
    cols: [["date", "Date"], ["invoice_no", "Invoice"], ["party", "Customer"], ["lines", "Items", "count"], ["taxable", "Taxable", "inr"], ["cgst", "CGST", "inr"], ["sgst", "SGST", "inr"], ["igst", "IGST", "inr"], ["total", "Total", "inr"], ["due_date", "Due"], ["paid", "Status", "paid"]],
  },
  purchases: {
    title: "Purchase Register", hint: "Supplier bills — eligible ITC tracked for GSTR-3B",
    init: inv,
    fields: [["date", "Date", "date"], ["party", "Supplier", "party:Supplier"], ["gstin", "Supplier GSTIN"], ["invoice_no", "Bill no."], ["due_date", "Due date (default +30d)", "date"], ["interstate", "Inter-state (IGST)", "check"]],
    cols: [["date", "Date"], ["invoice_no", "Bill"], ["party", "Supplier"], ["lines", "Items", "count"], ["taxable", "Taxable", "inr"], ["cgst", "CGST", "inr"], ["sgst", "SGST", "inr"], ["igst", "IGST", "inr"], ["total", "Total", "inr"], ["due_date", "Due"], ["paid", "Status", "paid"]],
  },
  cashbank: {
    title: "Cash & Bank Book", hint: "Receipts from customers & payments to suppliers",
    init: { date: "", txn_type: "Receipt", mode: "Bank", invoice_id: "", party: "", amount: 0, description: "" },
    fields: [["date", "Date", "date"], ["txn_type", "Type", ["Receipt", "Payment"]], ["mode", "Mode", ["Bank", "Cash"]], ["invoice_id", "Against invoice / bill", "invoice"], ["party", "Party", "party:"], ["amount", "Amount", "number"], ["description", "Narration"]],
    cols: [["date", "Date"], ["txn_type", "Type"], ["mode", "Mode"], ["party", "Party"], ["invoice_no", "Invoice"], ["description", "Narration"], ["amount", "Amount", "inr"]],
  },
  expenses: {
    title: "Expense Book", hint: "Day-to-day business expenses by head",
    init: { date: "", head: "Rent", mode: "Bank", amount: 0, description: "" },
    fields: [["date", "Date", "date"], ["head", "Expense head", EXP_HEADS], ["mode", "Paid via", ["Bank", "Cash"]], ["amount", "Amount", "number"], ["description", "Narration"]],
    cols: [["date", "Date"], ["head", "Head"], ["mode", "Mode"], ["description", "Narration"], ["amount", "Amount", "inr"]],
  },
};

const TABS = [
  ["overview", "Dashboard", SquaresFour], ["parties", "Parties", AddressBook], ["items", "Items", Package],
  ["sales", "Sales / Billing", Receipt], ["purchases", "Purchases", ShoppingCart], ["cashbank", "Cash & Bank", Bank],
  ["expenses", "Expenses", Wallet], ["reports", "Reports", ChartBar],
];

const inputCls = "w-full border border-black/20 rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F766E]";

function Field({ f, val, onChange, masters, form }) {
  const [key, label, type] = f;
  const tid = `bk-f-${key}`;
  if (type === "check") return (
    <label className="flex items-center gap-2 text-sm pt-5"><input data-testid={tid} type="checkbox" checked={!!val} onChange={(e) => onChange(key, e.target.checked)} /> {label}</label>
  );
  let input;
  if (type === "invoice") input = (
    <select data-testid={tid} value={val} onChange={(e) => onChange(key, e.target.value)} className={inputCls}>
      <option value="">— None —</option>
      {(form?.txn_type === "Payment" ? masters.bills : masters.invoices).map((x) => <option key={x.id} value={x.id}>{x.invoice_no || "Bill"} · {x.party} · due {inr((x.total || 0) - (x.amount_received || 0))}</option>)}
    </select>);
  else if (Array.isArray(type)) input = <select data-testid={tid} value={val} onChange={(e) => onChange(key, e.target.value)} className={inputCls}>{type.map((o) => <option key={o}>{o}</option>)}</select>;
  else if (typeof type === "string" && (type.startsWith("party") || type === "item")) {
    const list = type === "item" ? masters.items : masters.parties.filter((p) => !type.split(":")[1] || p.party_type === type.split(":")[1]);
    input = <><input data-testid={tid} list={`dl-${key}`} value={val} onChange={(e) => onChange(key, e.target.value)} className={inputCls} />
      <datalist id={`dl-${key}`}>{list.map((x) => <option key={x.id} value={x.name} />)}</datalist></>;
  } else input = <input data-testid={tid} type={type || "text"} value={val} onChange={(e) => onChange(key, type === "number" ? e.target.value : e.target.value)} className={inputCls} />;
  return <div><label className="label block mb-1">{label}</label>{input}</div>;
}

function LineItems({ lines, onChange, items }) {
  const upd = (i, k, v) => onChange(lines.map((l, j) => {
    if (j !== i) return l;
    const n = { ...l, [k]: v };
    if (k === "description") { const it = items.find((x) => x.name === v); if (it) Object.assign(n, { hsn: it.hsn, rate: it.rate, gst_rate: it.gst_rate }); }
    return n;
  }));
  const lineTotal = (l) => { const t = Number(l.qty || 0) * Number(l.rate || 0); return t + (t * Number(l.gst_rate || 0)) / 100; };
  return (
    <div className="col-span-2 md:col-span-5" data-testid="bk-lines">
      <p className="label mb-1">Items</p>
      <datalist id="dl-line-items">{items.map((x) => <option key={x.id} value={x.name} />)}</datalist>
      <div className="space-y-2">
        {lines.map((l, i) => (
          <div key={i} data-testid={`bk-line-${i}`} className="grid grid-cols-6 md:grid-cols-12 gap-2 items-center">
            <input data-testid={`bk-line-desc-${i}`} list="dl-line-items" placeholder="Item / service" value={l.description} onChange={(e) => upd(i, "description", e.target.value)} className={`${inputCls} col-span-6 md:col-span-4`} />
            <input data-testid={`bk-line-hsn-${i}`} placeholder="HSN/SAC" value={l.hsn} onChange={(e) => upd(i, "hsn", e.target.value)} className={`${inputCls} col-span-2 md:col-span-2`} />
            <input data-testid={`bk-line-qty-${i}`} type="number" placeholder="Qty" value={l.qty} onChange={(e) => upd(i, "qty", e.target.value)} className={`${inputCls} col-span-2 md:col-span-1`} />
            <input data-testid={`bk-line-rate-${i}`} type="number" placeholder="Rate" value={l.rate} onChange={(e) => upd(i, "rate", e.target.value)} className={`${inputCls} col-span-2 md:col-span-2`} />
            <select data-testid={`bk-line-gst-${i}`} value={l.gst_rate} onChange={(e) => upd(i, "gst_rate", e.target.value)} className={`${inputCls} col-span-2 md:col-span-1`}>{GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}</select>
            <span className="tnum text-sm text-right col-span-3 md:col-span-1">{inr(lineTotal(l))}</span>
            <button type="button" data-testid={`bk-line-del-${i}`} disabled={lines.length === 1} onClick={() => onChange(lines.filter((_, j) => j !== i))} className="text-[#bbb] hover:text-[#FF3B30] disabled:opacity-30 col-span-1 justify-self-end"><Trash size={14} /></button>
          </div>
        ))}
      </div>
      <button type="button" data-testid="bk-line-add" onClick={() => onChange([...lines, newLine()])} className="mt-2 text-xs text-[#0F766E] border border-[#0F766E]/40 hover:bg-[#0F766E]/5 px-3 py-1.5 rounded-sm inline-flex items-center gap-1"><Plus size={12} /> Add item</button>
    </div>
  );
}

const isSettled = (r) => !!r.paid || ((r.amount_received || 0) > 0 && (r.amount_received || 0) >= (r.total || 0) - 0.01);

function PaidCell({ r, i, overdue, onToggle, readOnly }) {
  const done = isSettled(r);
  const cls = done ? "bg-[#00A86B]/10 text-[#00A86B] border-[#00A86B]/30" : overdue ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30" : "bg-[#B8860B]/10 text-[#8a6d00] border-[#FFD700]";
  return (
    <button data-testid={`bk-paid-${i}`} disabled={readOnly} onClick={() => onToggle(r)} title={readOnly ? "" : "Click to toggle paid"}
      className={`text-xs px-2 py-0.5 rounded-sm border font-semibold ${cls}`}>{done ? "Paid" : overdue ? "Overdue" : "Unpaid"}</button>
  );
}

function Register({ kind, q, masters, onChanged, readOnly }) {
  const cfg = REGISTERS[kind];
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ ...cfg.init, date: cfg.init.date === "" ? today() : undefined });
  const load = () => api.get(`/bookkeeping/${kind}${q}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); setForm({ ...cfg.init, ...("date" in cfg.init ? { date: today() } : {}) }); }, [kind, q]);

  const set = (k, v) => setForm((p) => {
    const n = { ...p, [k]: v };
    if (k === "txn_type") n.invoice_id = "";
    if (k === "invoice_id" && v) { const iv = [...masters.invoices, ...masters.bills].find((x) => x.id === v); if (iv) Object.assign(n, { party: iv.party, amount: Math.round(((iv.total || 0) - (iv.amount_received || 0)) * 100) / 100 }); }
    if (k === "party") { const pt = masters.parties.find((x) => x.name === v); if (pt && "gstin" in n) n.gstin = pt.gstin || ""; }
    if (k === "description" && "hsn" in n) { const it = masters.items.find((x) => x.name === v); if (it) Object.assign(n, { hsn: it.hsn, rate: it.rate, gst_rate: it.gst_rate }); }
    return n;
  });
  const preview = form.lines ? form.lines.reduce((a, l) => {
    const t = Number(l.qty || 0) * Number(l.rate || 0); return { t: a.t + t, tax: a.tax + (t * Number(l.gst_rate || 0)) / 100 };
  }, { t: 0, tax: 0 }) : null;

  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, client_email: q ? decodeURIComponent(q.split("=")[1]) : undefined };
    ["qty", "rate", "gst_rate", "amount", "opening_stock"].forEach((k) => { if (k in body) body[k] = Number(body[k] || 0); });
    if (body.lines) body.lines = body.lines.map((l) => ({ ...l, qty: Number(l.qty || 0), rate: Number(l.rate || 0), gst_rate: Number(l.gst_rate || 0) }));
    try {
      await api.post(`/bookkeeping/${kind}`, body);
      toast.success(`${cfg.title}: entry saved`);
      setForm({ ...cfg.init, ...("date" in cfg.init ? { date: today() } : {}) }); load(); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save"); }
  };
  const del = async (r) => { await api.delete(`/bookkeeping/${kind}/${r.id}${q}`); toast.success("Entry deleted"); load(); onChanged(); };
  const statementPdf = async (r) => {
    const sep = q ? "&" : "?";
    try {
      const res = await api.get(`/bookkeeping-statement/pdf${q}${sep}party=${encodeURIComponent(r.name)}`, { responseType: "blob" });
      downloadBlob(res.data, `Statement_${r.name.replace(/[^a-z0-9]/gi, "_")}.pdf`);
    } catch { toast.error("Could not generate statement"); }
  };
  const printInv = async (r) => {
    try {
      const res = await api.get(`/bookkeeping/sales/${r.id}/pdf${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank");
      if (!w) downloadBlob(res.data, `${(r.invoice_no || "invoice").replace(/\//g, "-")}.pdf`);
    } catch { toast.error("Could not generate invoice"); }
  };
  const emailInv = async (r, to) => {
    const client_email = q ? decodeURIComponent(q.split("=")[1]) : undefined;
    try {
      const { data } = await api.post(`/bookkeeping/sales/${r.id}/email`, { client_email, to });
      toast.success(`Invoice ${r.invoice_no} emailed to ${data.to}`); load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (!to && typeof d === "string" && d.startsWith("No email")) {
        const addr = window.prompt(`No email saved for ${r.party}. Send invoice to:`);
        if (addr) emailInv(r, addr.trim());
      } else toast.error(typeof d === "string" ? d : "Could not email invoice");
    }
  };

  const ce = q ? decodeURIComponent(q.split("=")[1]) : undefined;
  const togglePaid = async (r) => {
    await api.patch(`/bookkeeping/${kind}/${r.id}/paid`, { paid: !r.paid, client_email: ce });
    toast.success(`${r.invoice_no || "Bill"} marked ${r.paid ? "unpaid" : "paid"}`); load(); onChanged();
  };
  const emailStatement = async (r, to) => {
    try {
      const { data } = await api.post("/bookkeeping-statement/email", { party: r.name, client_email: ce, to });
      toast.success(`Statement emailed to ${data.to} · balance ${inr(data.balance)}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (!to && typeof d === "string" && d.startsWith("No email")) {
        const addr = window.prompt(`No email saved for ${r.name}. Send statement to:`);
        if (addr) emailStatement(r, addr.trim());
      } else toast.error(typeof d === "string" ? d : "Could not send statement");
    }
  };
  const remindAll = async () => {
    try {
      const { data } = await api.post("/bookkeeping-reminders", { client_email: ce });
      toast.success(data.sent ? `Sent ${data.sent} payment reminder(s)` : "No overdue invoices need a reminder right now");
      if (data.skipped_no_email) toast.warning(`${data.skipped_no_email} overdue invoice(s) skipped — customer has no email`);
      load();
    } catch { toast.error("Could not send reminders"); }
  };
  const overdueCount = kind === "sales" ? rows.filter((r) => !isSettled(r) && r.due_date && r.due_date < today()).length : 0;

  return (
    <div data-testid={`bk-register-${kind}`}>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3"><div><p className="font-head font-bold text-lg">{cfg.title}</p><p className="text-xs text-[#888]">{cfg.hint}</p></div>
        {kind === "sales" && !readOnly && (
          <button data-testid="bk-remind-all" onClick={remindAll} className="border border-[#F5A623] text-[#8a6d00] hover:bg-[#F5A623]/10 px-3 py-2 rounded-sm text-sm inline-flex items-center gap-2">
            <EnvelopeSimple size={15} /> Send payment reminders{overdueCount ? ` (${overdueCount} overdue)` : ""}</button>
        )}
      </div>
      {!readOnly && (
        <form onSubmit={save} className="bg-[#0F766E]/[0.04] border border-[#0F766E]/20 rounded-sm p-4 mb-5 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          {cfg.fields.map((f) => <Field key={f[0]} f={f} val={form[f[0]] ?? ""} onChange={set} masters={masters} form={form} />)}
          {form.lines && <LineItems lines={form.lines} items={masters.items} onChange={(lines) => setForm((p) => ({ ...p, lines }))} />}
          {preview && <p className="text-xs text-[#555] md:col-span-3 self-center tnum" data-testid="bk-preview">Taxable {inr(preview.t)} · GST {inr(preview.tax)} · <b>Total {inr(preview.t + preview.tax)}</b></p>}
          <button data-testid="bk-save" className="bg-[#0F766E] hover:brightness-95 text-white px-4 py-2 rounded-sm text-sm inline-flex items-center justify-center gap-1 transition-all"><Plus size={14} /> Save entry</button>
        </form>
      )}
      {kind === "cashbank" && !readOnly && <BankImport q={q} parties={masters.parties} onDone={() => { load(); onChanged(); }} />}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="label text-left border-b border-black/10">{cfg.cols.map((c) => <th key={c[0]} className={`pb-2 px-2 ${c[2] && c[2] !== "paid" ? "text-right" : ""}`}>{c[1]}</th>)}<th className="w-8" /></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} data-testid={`bk-row-${kind}-${i}`} className="border-b border-black/5">
                {cfg.cols.map((c) => <td key={c[0]} className={`py-2 px-2 ${c[2] && c[2] !== "paid" ? "text-right tnum" : ""}`}>{c[2] === "paid" ? <PaidCell r={r} i={i} overdue={!isSettled(r) && r.due_date && r.due_date < today()} onToggle={togglePaid} readOnly={readOnly} /> : c[2] === "count" ? ((r.lines || []).length || 1) : c[2] ? inr(r[c[0]] || 0) : (r[c[0]] || "—")}</td>)}
                <td className="text-right whitespace-nowrap">
                  {kind === "parties" && r.party_type === "Customer" && <button data-testid={`bk-statement-pdf-${i}`} onClick={() => statementPdf(r)} title="Download statement PDF" className="text-[#0F766E] hover:bg-[#0F766E]/10 p-1 rounded-sm mr-1"><FilePdf size={15} /></button>}
                  {kind === "parties" && r.party_type === "Customer" && !readOnly && <button data-testid={`bk-statement-${i}`} onClick={() => emailStatement(r)} title="Email statement of account" className="text-[#0F766E] hover:bg-[#0F766E]/10 p-1 rounded-sm mr-1 inline-flex items-center gap-1 text-xs"><EnvelopeSimple size={14} /> Statement</button>}
                  {kind === "sales" && <button data-testid={`bk-print-${i}`} onClick={() => printInv(r)} title="Print tax invoice" className="text-[#0F766E] hover:bg-[#0F766E]/10 p-1 rounded-sm mr-1"><Printer size={15} /></button>}
                  {kind === "sales" && !readOnly && <button data-testid={`bk-email-${i}`} onClick={() => emailInv(r)} title={r.emailed_to ? `Emailed to ${r.emailed_to}` : "Email invoice"} className={`p-1 rounded-sm mr-1 hover:bg-[#0F766E]/10 ${r.emailed_to ? "text-[#00A86B]" : "text-[#0F766E]"}`}><EnvelopeSimple size={15} weight={r.emailed_to ? "fill" : "regular"} /></button>}
                  {!readOnly && <button data-testid={`bk-del-${kind}-${i}`} onClick={() => del(r)} className="text-[#bbb] hover:text-[#FF3B30]"><Trash size={14} /></button>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={cfg.cols.length + 1} className="py-6 text-center text-xs text-[#888]">No entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return <div className="card p-4"><p className="label mb-1">{label}</p><p className={`font-head font-black text-xl tnum ${tone || ""}`}>{value}</p></div>;
}

function Ageing({ ag, id = "bk-ageing", title = "Receivables Ageing", who = "customers" }) {
  if (!ag) return null;
  const cols = [["not_due", "Not yet due", "#00A86B"], ["d0_30", "1–30 days overdue", "#F5A623"], ["d31_60", "31–60 days", "#EA580C"], ["d60_plus", "60+ days", "#DC2626"]];
  return (
    <div className="card p-5 mb-5" data-testid={id}>
      <div className="flex items-end justify-between mb-3"><p className="label">{title}</p><p className="text-sm">Outstanding <b className="tnum" data-testid={`${id}-total`}>{inr(ag.total)}</b></p></div>
      <div className="flex h-3 w-full bg-black/5 mb-4 overflow-hidden rounded-sm">
        {cols.map(([k, , c]) => ag.total > 0 && ag.buckets[k].amount > 0 && <div key={k} style={{ width: `${(ag.buckets[k].amount / ag.total) * 100}%`, background: c }} />)}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        {cols.map(([k, l, c]) => (
          <div key={k} data-testid={`${id}-${k}`} className="border-l-4 pl-3" style={{ borderColor: c }}>
            <p className="text-xs text-[#888]">{l}</p>
            <p className="font-head font-bold tnum">{inr(ag.buckets[k].amount)}</p>
            <p className="text-xs text-[#888]">{ag.buckets[k].count} invoice(s)</p>
          </div>
        ))}
      </div>
      {ag.top_overdue.length > 0 && (
        <div className="border-t border-black/10 pt-3">
          <p className="label mb-2">Top overdue {who}</p>
          {ag.top_overdue.map((p) => (
            <div key={p.party} className="flex justify-between text-sm py-1"><span>{p.party} <span className="text-xs text-[#888]">· up to {p.max_days} days</span></span><span className="tnum font-semibold text-[#DC2626]">{inr(p.amount)}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

function Forecast({ fc }) {
  if (!fc) return null;
  const tone = (v) => (v < 0 ? "text-[#DC2626]" : "text-[#00A86B]");
  const rows = [["Overdue (expected now)", "overdue"], ["Due in next 30 days", "d30"], ["Due in 31–60 days", "d60"]];
  return (
    <div className="card p-5 mb-5" data-testid="bk-forecast">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div><p className="label">Cash Flow Forecast</p><p className="text-xs text-[#888]">From cash & bank balance plus unpaid invoices (in) and bills (out)</p></div>
        <div className="flex gap-6">
          <div><p className="text-xs text-[#888]">Today</p><p className={`font-head font-black text-xl tnum ${tone(fc.opening)}`} data-testid="bk-fc-opening">{inr(fc.opening)}</p></div>
          <div><p className="text-xs text-[#888]">In 30 days</p><p className={`font-head font-black text-xl tnum ${tone(fc.projected_30)}`} data-testid="bk-fc-30">{inr(fc.projected_30)}</p></div>
          <div><p className="text-xs text-[#888]">In 60 days</p><p className={`font-head font-black text-xl tnum ${tone(fc.projected_60)}`} data-testid="bk-fc-60">{inr(fc.projected_60)}</p></div>
        </div>
      </div>
      <div className="overflow-x-auto"><table className="w-full text-sm min-w-[420px]">
        <thead><tr className="label text-left border-b border-black/10"><th className="pb-2">Window</th><th className="pb-2 text-right">Money in</th><th className="pb-2 text-right">Money out</th><th className="pb-2 text-right">Net</th></tr></thead>
        <tbody>{rows.map(([l, k]) => {
          const net = fc.inflow[k] - fc.outflow[k];
          return <tr key={k} className="border-b border-black/5" data-testid={`bk-fc-row-${k}`}><td className="py-2">{l}</td><td className="text-right tnum text-[#00A86B]">{inr(fc.inflow[k])}</td><td className="text-right tnum text-[#DC2626]">{inr(fc.outflow[k])}</td><td className={`text-right tnum font-semibold ${tone(net)}`}>{inr(net)}</td></tr>;
        })}</tbody>
      </table></div>
      {fc.projected_30 < 0 && <p className="text-xs text-[#DC2626] mt-3" data-testid="bk-fc-warning">Heads-up: balance may go negative within 30 days — follow up on receivables or reschedule supplier payments.</p>}
    </div>
  );
}

function Overview({ rep, go }) {
  if (!rep) return <p className="label">Loading…</p>;
  return (
    <div data-testid="bk-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Stat label="Sales (taxable)" value={inr(rep.sales_register.taxable)} />
        <Stat label="Purchases (taxable)" value={inr(rep.purchase_register.taxable)} />
        <Stat label="Net GST payable" value={inr(Object.values(rep.gstr3b.net).reduce((a, b) => a + b, 0))} tone="text-[#DC2626]" />
        <Stat label="Net profit" value={inr(rep.pnl.net_profit)} tone={rep.pnl.net_profit >= 0 ? "text-[#00A86B]" : "text-[#DC2626]"} />
        <Stat label="Bank balance" value={inr(rep.bank_book.balance)} />
        <Stat label="Cash in hand" value={inr(rep.cash_book.balance)} />
        <Stat label="Parties / Items" value={`${rep.counts.parties} / ${rep.counts.items}`} />
        <Stat label="Invoices / Bills" value={`${rep.counts.sales} / ${rep.counts.purchases}`} />
      </div>
      <div className="grid lg:grid-cols-2 gap-x-4">
        <Ageing ag={rep.ageing} />
        <Ageing ag={rep.payables_ageing} id="bk-payables" title="Payables Ageing (to suppliers)" who="suppliers" />
      </div>
      <Forecast fc={rep.forecast} />
      <p className="label mb-2">Quick actions</p>
      <div className="flex flex-wrap gap-2">
        {[["sales", "Create sales invoice"], ["purchases", "Record purchase"], ["cashbank", "Record receipt / payment"], ["reports", "View GST reports"]].map(([k, l]) => (
          <button key={k} data-testid={`bk-quick-${k}`} onClick={() => go(k)} className="border border-[#0F766E]/40 text-[#0F766E] hover:bg-[#0F766E]/5 px-3 py-2 rounded-sm text-sm transition-colors">{l}</button>
        ))}
      </div>
    </div>
  );
}

function KV({ rows }) {
  return <div className="divide-y divide-black/5 text-sm">{rows.map(([k, v, b]) => <div key={k} className={`flex justify-between py-1.5 ${b ? "font-semibold" : ""}`}><span>{k}</span><span className="tnum">{v}</span></div>)}</div>;
}

function Reports({ rep, q }) {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  if (!rep) return <p className="label">Loading…</p>;
  const g3 = rep.gstr3b;
  const pdf = async () => {
    try { const r = await api.get(`/bookkeeping-reports/pdf${q}`, { responseType: "blob" }); downloadBlob(r.data, "Bookkeeping_Report.pdf"); }
    catch { toast.error("Could not generate report"); }
  };
  const exportJson = async (form) => {
    const sep = q ? "&" : "?";
    try {
      const r = await api.get(`/bookkeeping-reports/gstr/${form}${q}${sep}period=${period}`, { responseType: "blob" });
      const fp = period.slice(5, 7) + period.slice(0, 4);
      downloadBlob(r.data, `${form.toUpperCase()}_${fp}.json`);
      toast.success(`${form.toUpperCase()} JSON downloaded for ${period}`);
    } catch { toast.error("Could not export return"); }
  };
  return (
    <div data-testid="bk-reports">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div><p className="font-head font-bold text-lg">Reports generated from Bookkeeping software</p>
          <p className="text-xs text-[#888]">Live from your registers — GSTR-1, GSTR-3B, ledgers, stock & P&L</p></div>
        <button data-testid="bk-pdf" onClick={pdf} className="bg-[#111] hover:bg-black text-white px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2"><FilePdf size={16} /> Download PDF</button>
      </div>
      <div className="card p-4 mb-4 flex flex-wrap items-center gap-3 bg-[#0F766E]/[0.04]" data-testid="bk-gstr-export">
        <FileCode size={20} className="text-[#0F766E]" />
        <div className="flex-1 min-w-[200px]"><p className="text-sm font-semibold">Export GST return JSON</p>
          <p className="text-xs text-[#888]">Upload the file using the GST portal's Returns Offline Tool. Add your business GSTIN in Settings first.</p></div>
        <input data-testid="bk-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="border border-black/20 rounded-sm px-2 py-1.5 text-sm" />
        <button data-testid="bk-export-gstr1" onClick={() => exportJson("gstr1")} className="bg-[#0F766E] hover:brightness-95 text-white px-3 py-2 rounded-sm text-sm">GSTR-1 JSON</button>
        <button data-testid="bk-export-gstr3b" onClick={() => exportJson("gstr3b")} className="bg-[#0F766E] hover:brightness-95 text-white px-3 py-2 rounded-sm text-sm">GSTR-3B JSON</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card p-5" data-testid="rep-gstr1"><p className="label mb-2">GSTR-1 · Outward supplies</p>
          <KV rows={[[`B2B invoices (${rep.gstr1.b2b.count})`, inr(rep.gstr1.b2b.taxable)], ["B2B tax", inr(rep.gstr1.b2b.tax)],
            [`B2C invoices (${rep.gstr1.b2c.count})`, inr(rep.gstr1.b2c.taxable)], ["B2C tax", inr(rep.gstr1.b2c.tax)]]} /></div>
        <div className="card p-5" data-testid="rep-gstr3b"><p className="label mb-2">GSTR-3B · Tax liability</p>
          <table className="w-full text-sm"><thead><tr className="label text-left"><th></th><th className="text-right">Output</th><th className="text-right">ITC</th><th className="text-right">Net payable</th></tr></thead>
            <tbody>{["igst", "cgst", "sgst"].map((k) => <tr key={k} className="border-t border-black/5"><td className="py-1.5 uppercase">{k}</td><td className="text-right tnum">{inr(g3.output[k])}</td><td className="text-right tnum">{inr(g3.itc[k])}</td><td className="text-right tnum font-semibold">{inr(g3.net[k])}</td></tr>)}</tbody></table></div>
        <div className="card p-5" data-testid="rep-pnl"><p className="label mb-2">Profit & Loss</p>
          <KV rows={[["Sales (net of GST)", inr(rep.pnl.sales)], ["Less: Purchases", inr(rep.pnl.purchases)], ["Gross profit", inr(rep.pnl.gross_profit), 1],
            ...Object.entries(rep.expense_heads).map(([h, v]) => [`Less: ${h}`, inr(v)]), ["Net profit", inr(rep.pnl.net_profit), 1]]} /></div>
        <div className="card p-5" data-testid="rep-cashbank"><p className="label mb-2">Cash & Bank Book</p>
          <KV rows={[["Bank receipts", inr(rep.bank_book.receipts)], ["Bank payments", inr(rep.bank_book.payments)], ["Bank balance", inr(rep.bank_book.balance), 1],
            ["Cash receipts", inr(rep.cash_book.receipts)], ["Cash payments", inr(rep.cash_book.payments)], ["Cash in hand", inr(rep.cash_book.balance), 1]]} /></div>
        <div className="card p-5" data-testid="rep-ledgers"><p className="label mb-2">Party Ledgers · Outstanding</p>
          {rep.ledgers.length === 0 ? <p className="text-xs text-[#888]">No party transactions yet.</p> :
            <KV rows={rep.ledgers.map((l) => [`${l.party} · ${l.type === "Customer" ? "Receivable" : "Payable"}`, inr(l.balance)])} />}</div>
        <div className="card p-5" data-testid="rep-stock"><p className="label mb-2">Stock Summary</p>
          {rep.stock.length === 0 ? <p className="text-xs text-[#888]">Add items in the Item master to track stock.</p> : (
            <table className="w-full text-sm"><thead><tr className="label text-left"><th>Item</th><th className="text-right">Open</th><th className="text-right">In</th><th className="text-right">Out</th><th className="text-right">Close</th></tr></thead>
              <tbody>{rep.stock.map((s) => <tr key={s.item} className="border-t border-black/5"><td className="py-1.5">{s.item}</td><td className="text-right tnum">{s.opening}</td><td className="text-right tnum">{s.inward}</td><td className="text-right tnum">{s.outward}</td><td className="text-right tnum font-semibold">{s.closing}</td></tr>)}</tbody></table>)}</div>
      </div>
    </div>
  );
}

export default function Bookkeeping({ clientEmail, readOnly = false }) {
  const [tab, setTab] = useState("overview");
  const [rep, setRep] = useState(null);
  const [masters, setMasters] = useState({ parties: [], items: [], invoices: [], bills: [] });
  const q = useMemo(() => (clientEmail ? `?client_email=${encodeURIComponent(clientEmail)}` : ""), [clientEmail]);

  const refresh = () => {
    api.get(`/bookkeeping-reports${q}`).then((r) => setRep(r.data)).catch(() => setRep(null));
    Promise.all([api.get(`/bookkeeping/parties${q}`), api.get(`/bookkeeping/items${q}`), api.get(`/bookkeeping/sales${q}`), api.get(`/bookkeeping/purchases${q}`)])
      .then(([p, i, s, b]) => setMasters({ parties: p.data, items: i.data, invoices: s.data.filter((x) => !isSettled(x)), bills: b.data.filter((x) => !isSettled(x)) })).catch(() => {});
  };
  useEffect(() => { refresh(); }, [q]);

  return (
    <div>
      <PageHead eyebrow="Bookkeeping Services · GST Billing & Accounting" title="Bookkeeping" />
      <div className="card overflow-hidden" data-testid="bk-software">
        <div className="bg-[#0F766E] text-white px-5 py-3 flex items-center gap-2">
          <BookOpenText size={20} weight="fill" />
          <span className="font-head font-bold">GST Accounting & Billing</span>
          <span className="text-xs text-white/70 hidden sm:inline">· Free accounting & billing utility for small taxpayers</span>
        </div>
        <div className="flex overflow-x-auto border-b border-black/10 bg-black/[0.02]" data-testid="bk-tabs">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} data-testid={`bk-tab-${k}`} onClick={() => setTab(k)}
              className={`shrink-0 px-4 py-3 text-sm inline-flex items-center gap-2 border-b-2 transition-colors ${tab === k ? "border-[#0F766E] text-[#0F766E] bg-white font-semibold" : "border-transparent text-[#555] hover:text-[#111]"}`}>
              <Icon size={16} /> {l}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === "overview" && <Overview rep={rep} go={setTab} />}
          {REGISTERS[tab] && <Register kind={tab} q={q} masters={masters} onChanged={refresh} readOnly={readOnly} />}
          {tab === "reports" && <Reports rep={rep} q={q} />}
        </div>
      </div>
    </div>
  );
}
